import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Button, Alert, Tag } from 'antd';
import { FileSearchOutlined, UserAddOutlined, UserDeleteOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import DataGrid from '../../DataGrid/DataGrid.jsx';
import {
  dataGridDateSorter,
  dataGridTextSorter,
} from '../../DataGrid/sorters/DataGridSorters.jsx';
import { DATETIME_FORMAT_DATETIME } from '../../../../utils/helpers/CommonHelper.jsx';
import { IconButton } from '../../controls/Controls.jsx';
import { Spinner } from '../../index.jsx';
import {
  StyledModal,
  StyledModalHeader,
} from '../../form/FormElements.jsx';
import HtmlContentWithAntdImages from '../../Image/HtmlContentWithAntdImages.jsx';
import { useGetWorkshopQuery } from '../../../../redux/services/fo_workshops.jsx';
import {
  useSignIntoDateMutation,
} from '../../../../redux/services/fo_dates.jsx';
import useUserHasRole from '../../../../hooks/useUserHasRoles.jsx';
import { ROLE_FRI_OPEN_PARTICIPANT } from '../../../../utils/constants/roles.js';
import { useToastMessages } from '../../../../hooks/useToastMessages.jsx';
import { routes } from '../../../../utils/helpers/ApiClient.jsx';
import QuestionModal from '../../modals/QuestionModal/QuestionModal.jsx';

const EventDatesTable = ({ dates, onRowClick }) => {
  const { t } = useTranslation();
  const [selectedWorkshopId, setSelectedWorkshopId] = useState(null);
  const [dateToSignIn, setDateToSignIn] = useState(null);
  const [dateToUnsign, setDateToUnsign] = useState(null);
  const [isGuestModalOpen, setIsGuestModalOpen] = useState(false);
  const lastRegistrationActionRef = useRef(null);

  const user = useSelector((state) => state.auth?.user);
  const navigate = useNavigate();
  const location = useLocation();
  const isFriOpenParticipant = useUserHasRole(ROLE_FRI_OPEN_PARTICIPANT);
  const canUseParticipantRegistrationState = Boolean(user) && isFriOpenParticipant;
  const { successMessage, apiErrorMessage } = useToastMessages();

  const [signIntoDate, { isSuccess, isError, error }] = useSignIntoDateMutation();

  const normalizedDates = Array.isArray(dates)
    ? dates
    : Array.isArray(dates?.data)
      ? dates.data
      : [];

  const sameWorkshopConflicts = useMemo(() => {
    if (!dateToSignIn || !canUseParticipantRegistrationState) {
      return [];
    }

    return normalizedDates.filter(
      (d) =>
        d.is_user_registered &&
        d.workshop?.id === dateToSignIn.workshop?.id &&
        d.id !== dateToSignIn.id,
    );
  }, [dateToSignIn, normalizedDates, canUseParticipantRegistrationState]);

  const signInConflicts = useMemo(() => {
    if (!dateToSignIn || !canUseParticipantRegistrationState) {
      return [];
    }

    const selectedFrom = dayjs(dateToSignIn.date_from);
    const selectedTo = dayjs(dateToSignIn.date_to);

    if (!selectedFrom.isValid() || !selectedTo.isValid()) {
      return [];
    }

    return normalizedDates.filter((date) => {
      if (!date.is_user_registered || date.id === dateToSignIn.id) {
        return false;
      }

      // Same-workshop conflicts are shown in a dedicated alert block.
      if (date.workshop?.id === dateToSignIn.workshop?.id) {
        return false;
      }

      const conflictFrom = dayjs(date.date_from);
      const conflictTo = dayjs(date.date_to);

      if (!conflictFrom.isValid() || !conflictTo.isValid()) {
        return false;
      }

      return selectedFrom.isBefore(conflictTo) && selectedTo.isAfter(conflictFrom);
    });
  }, [dateToSignIn, normalizedDates, canUseParticipantRegistrationState]);

  useEffect(() => {
    if (isSuccess) {
      if (lastRegistrationActionRef.current === 'unregister') {
        successMessage({ message: t('Boli ste úspešne odhlásený z termínu') });
      } else {
        successMessage({ message: t('Boli ste úspešne prihlásený na termín') });
      }
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      apiErrorMessage(error);
    }
  }, [isError, error]);

  const openWorkshopDetail = (workshopId) => {
    if (workshopId === null || workshopId === undefined) {
      return;
    }
    setSelectedWorkshopId(workshopId);
  };

  const handleSignInClick = (date, event) => {
    event.stopPropagation();
    if (!user) {
      setIsGuestModalOpen(true);
      return;
    }
    if (date.is_user_registered) {
      setDateToUnsign(date);
      return;
    }
    setDateToSignIn(date);
  };


  const {
    data: selectedWorkshop,
    isLoading: isLoadingWorkshop,
  } = useGetWorkshopQuery(selectedWorkshopId, {
    skip: selectedWorkshopId === null,
  });

  const getDateTimestamp = (dateValue) => {
    if (!dateValue) {
      return Number.MAX_SAFE_INTEGER;
    }
    const parsedDate = dayjs(dateValue);
    return parsedDate.isValid() ? parsedDate.valueOf() : Number.MAX_SAFE_INTEGER;
  };

  const groupedDates = useMemo(() => {
    const groups = new Map();

    normalizedDates.forEach((date) => {
      const workshopId = date?.workshop?.id ?? `workshop-${date?.id ?? 'unknown'}`;
      const workshopName = date?.workshop?.name ?? t('Neznámy seminár');
      const dateFromTimestamp = getDateTimestamp(date?.date_from);

      if (!groups.has(workshopId)) {
        groups.set(workshopId, {
          workshopId,
          workshopName,
          firstDateTimestamp: dateFromTimestamp,
          dates: [],
        });
      }

      const group = groups.get(workshopId);
      group.dates.push(date);
      group.firstDateTimestamp = Math.min(group.firstDateTimestamp, dateFromTimestamp);
    });

    return Array.from(groups.values())
      .map((group) => ({
        ...group,
        dates: [...group.dates].sort(
          (a, b) => getDateTimestamp(a?.date_from) - getDateTimestamp(b?.date_from),
        ),
      }))
      .sort((a, b) => {
        if (a.firstDateTimestamp !== b.firstDateTimestamp) {
          return a.firstDateTimestamp - b.firstDateTimestamp;
        }
        return a.workshopName.localeCompare(b.workshopName, 'sk');
      });
  }, [normalizedDates, t]);

  const columns = useMemo(
    () => [
      {
        title: t('Miestnosť'),
        dataIndex: 'location',
        ellipsis: true,
        sorter: dataGridTextSorter('location'),
      },
      {
        title: t('Obsadenosť'),
        dataIndex: 'capacity',
        ellipsis: true,
        sorter: (a, b) => {
          const aCurrent = Number(a.participants_count ?? 0);
          const bCurrent = Number(b.participants_count ?? 0);
          const aCapacity = Number(a.capacity ?? 0);
          const bCapacity = Number(b.capacity ?? 0);
          if (aCapacity === 0 || bCapacity === 0) {
            return aCurrent - bCurrent;
          }
          return aCurrent / aCapacity - bCurrent / bCapacity;
        },
        render: (_, record) => {
          const currentCapacity = Number(record.participants_count ?? 0);
          const capacity = Number(record.capacity ?? 0);
          return `${currentCapacity}/${capacity}`;
        },
      },
      {
        title: t('Začiatok'),
        dataIndex: 'date_from',
        ellipsis: true,
        sorter: dataGridDateSorter('date_from'),
        defaultSortOrder: 'ascend',
        render: (value) =>
          value ? dayjs(value).format(DATETIME_FORMAT_DATETIME) : '',
      },
      {
        title: t('Koniec'),
        dataIndex: 'date_to',
        ellipsis: true,
        sorter: dataGridDateSorter('date_to'),
        render: (value) =>
          value ? dayjs(value).format(DATETIME_FORMAT_DATETIME) : '',
      },
      ...(canUseParticipantRegistrationState
        ? [
            {
              title: t('Stav'),
              dataIndex: 'is_user_registered',
              ellipsis: true,
              sorter: (a, b) =>
                Number(Boolean(a?.is_user_registered)) -
                Number(Boolean(b?.is_user_registered)),
              render: (_, record) => {
                const currentCapacity = Number(record?.participants_count ?? 0);
                const capacity = Number(record?.capacity ?? 0);
                const isRegistered = Boolean(record?.is_user_registered);
                const isFull = capacity > 0 && currentCapacity >= capacity;

                if (!isRegistered && isFull) {
                  return <Tag color="warning">{t('Termín plný')}</Tag>;
                }

                return isRegistered ? (
                  <Tag color="success">{t('Prihlásený')}</Tag>
                ) : (
                  <Tag color="error">{t('Neprihlásený')}</Tag>
                );
              },
            },
          ]
        : []),
    ],
    [t, canUseParticipantRegistrationState],
  );

  return (
    <>
      {groupedDates.map((group) => (
        <div key={group.workshopId} style={{ marginBottom: '16px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '8px',
            }}
          >
            <button
              type="button"
              className="second-title second-title--interactive"
              onClick={(event) => {
                event.stopPropagation();
                openWorkshopDetail(group.workshopId);
              }}
              style={{
                cursor: 'pointer',
                margin: 0,
                border: 0,
                background: 'transparent',
                paddingBottom: 0,
              }}
            >
              {group.workshopName}
            </button>
            <IconButton
              icon={<FileSearchOutlined />}
              tooltip={t('Zobraziť detail seminára')}
              onClick={(event) => {
                event.stopPropagation();
                openWorkshopDetail(group.workshopId);
              }}
            />
          </div>
          <DataGrid
            rowKey={(date) => date.id}
            columns={columns}
            dataSource={group.dates}
            onRowClick={onRowClick}
            pagination={false}
            rowClassName={(record) =>
              canUseParticipantRegistrationState && record.is_user_registered
                ? 'event-date-row--registered'
                : ''
            }
            customSettings={
              !user || isFriOpenParticipant
                ? (record) => (
                    (() => {
                      const isRegistered =
                        canUseParticipantRegistrationState && record.is_user_registered;
                      const currentCapacity = Number(record?.participants_count ?? 0);
                      const capacity = Number(record?.capacity ?? 0);
                      const isFull = capacity > 0 && currentCapacity >= capacity;
                      const isSignUpDisabled = !isRegistered && isFull;

                      return (
                    <IconButton
                      icon={
                        isRegistered
                          ? <UserDeleteOutlined />
                          : <UserAddOutlined />
                      }
                      tooltip={
                        isRegistered
                          ? t('Odhlásiť sa z termínu')
                          : isSignUpDisabled
                            ? t('Termín je plný')
                            : t('Prihlásiť sa na termín')
                      }
                      disabledTooltip={t('Nie je možné sa prihlásiť')}
                      disabled={isSignUpDisabled}
                      onClick={(event) => handleSignInClick(record, event)}
                    />
                      );
                    })()
                  )
                : undefined
            }
          />
        </div>
      ))}

      {/* Workshop detail modal */}
      <StyledModal
        open={selectedWorkshopId !== null}
        width="100%"
        className="hp-news-modal-container news-modal-wrapper add-icons-to-file-anchors"
        style={{ maxWidth: '900px' }}
        onCancel={() => setSelectedWorkshopId(null)}
        footer={null}
      >
        {isLoadingWorkshop ? (
          <div
            style={{
              height: '100px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Spinner />
          </div>
        ) : (
          selectedWorkshop && (
            <>
              <StyledModalHeader>
                <a className="second-title title" href="#">
                  {selectedWorkshop?.name}
                </a>
              </StyledModalHeader>

              <div style={{ padding: '10px' }}>
                <div style={{ marginBottom: '10px' }}>
                  <strong>{t('Názov akcie')}:</strong>{' '}
                  {selectedWorkshop?.event?.name || '-'}
                </div>

                <div style={{ marginBottom: '10px' }}>
                  <strong>{t('Trvanie akcie')}:</strong>{' '}
                  {selectedWorkshop?.event?.date_from
                    ? dayjs(selectedWorkshop.event.date_from).format('DD.MM.YYYY')
                    : '-'}
                  {' - '}
                  {selectedWorkshop?.event?.date_to
                    ? dayjs(selectedWorkshop.event.date_to).format('DD.MM.YYYY')
                    : '-'}
                </div>

                <div style={{ marginBottom: '10px' }}>
                  <strong>{t('Lektor seminára')}:</strong>{' '}
                  {selectedWorkshop?.lecturer?.displayname || '-'}
                </div>

                <div>
                  <strong>{t('Popis')}:</strong>
                  <div style={{ marginTop: '6px' }}>
                    <HtmlContentWithAntdImages
                      content={selectedWorkshop?.description || ''}
                    />
                  </div>
                </div>
              </div>
            </>
          )
        )}
      </StyledModal>

      <QuestionModal
        visible={isGuestModalOpen}
        toggle={() => setIsGuestModalOpen(false)}
        headerTitle={t('Prihlásenie na termín')}
        question={t('Ak sa chete prihlásiť na termín musíte byť prihlásený do portálu FRI (ak už máte účet) alebo za zaregistrovať (ak ešte nemáte účet).')}
        showDefaultButtons={false}
        customButtons={
          <>
            <Button
              type="primary"
              onClick={() => {
                setIsGuestModalOpen(false);
                navigate(`${routes.externalUserRegistration}?from=fri-open`);
              }}
            >
              {t('Registrovať')}
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setIsGuestModalOpen(false);
                navigate(routes.loginPage, { state: { from: location.pathname } });
              }}
            >
              {t('Prihlásiť sa')}
            </Button>
          </>
        }
      />

      <QuestionModal
        visible={dateToUnsign !== null}
        toggle={() => setDateToUnsign(null)}
        headerTitle={t('Odhlásenie z termínu')}
        question={t('Naozaj sa chcete odhlásiť z vybraného termínu?')}
        yes={t('Áno, odhlásiť sa')}
        yesDanger={true}
        chosenPositive={() => {
          lastRegistrationActionRef.current = 'unregister';
          signIntoDate({ dateId: dateToUnsign.id, action: 'unregister' });
        }}
      />

      <QuestionModal
        visible={dateToSignIn !== null}
        toggle={() => setDateToSignIn(null)}
        headerTitle={t('Prihlásenie na termín')}
        yes={t('Áno, prihlásiť sa')}
        yesDisabled={signInConflicts.length > 0 || sameWorkshopConflicts.length > 0}
        question={
          <>
            <p>{t('Naozaj sa chcete prihlásiť na vybraný termín?')}</p>

            {sameWorkshopConflicts.length > 0 && (
              <Alert
                type="error"
                showIcon
                style={{ marginTop: '10px' }}
                title={t('Ste už prihlásený na iný termín tohto seminára:')}
                description={
                  <ul style={{ margin: '4px 0 0', paddingLeft: '20px' }}>
                    {sameWorkshopConflicts.map((d) => (
                      <li key={d.id}>
                        <strong>{d.name}</strong>
                        {' ('}
                        {dayjs(d.date_from).format(DATETIME_FORMAT_DATETIME)}
                        {' – '}
                        {dayjs(d.date_to).format(DATETIME_FORMAT_DATETIME)}
                        {')'}
                      </li>
                    ))}
                  </ul>
                }
              />
            )}

            {signInConflicts.length > 0 && (
              <Alert
                type="error"
                showIcon
                style={{ marginTop: '10px' }}
                title={t('Zvolený termín koliduje s termínom, na ktorý ste už prihlásený:')}
                description={
                  <ul style={{ margin: '4px 0 0', paddingLeft: '20px' }}>
                    {signInConflicts.map((conflict) => (
                      <li key={conflict.id}>
                        <strong>{conflict.workshop?.name ?? t('Neznámy seminár')}</strong>
                        {' ('}
                        {dayjs(conflict.date_from).format(DATETIME_FORMAT_DATETIME)}
                        {' – '}
                        {dayjs(conflict.date_to).format(DATETIME_FORMAT_DATETIME)}
                        {')'}
                      </li>
                    ))}
                  </ul>
                }
              />
            )}
          </>
        }
        chosenPositive={() => {
          lastRegistrationActionRef.current = 'register';
          signIntoDate({ dateId: dateToSignIn.id, action: 'register' });
        }}
      />
    </>
  );
};

EventDatesTable.propTypes = {
  dates: PropTypes.array,
  onRowClick: PropTypes.func,
};

export default EventDatesTable;

